import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { X, Eye, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { templatesAPI, getErrorMessage } from '@/services/api';
import { debounce } from '@/utils/debounce';

export interface TemplateApplyPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentText: string;
  platform?: string;
  onApply: (result: {
    text: string;
    first_comment?: string;
    hashtags: string[];
  }) => void;
}

interface Template {
  id: string;
  name: string;
  category: string;
  content: string;
  usage_count: number;
}

interface ApplyTemplateResult {
  text: string;
  first_comment?: string;
  hashtags: string[];
}

const CATEGORIES = ['Hooks', 'CTA', 'Hashtags', 'First Comment'];

export const TemplateApplyPanel: React.FC<TemplateApplyPanelProps> = ({
  isOpen,
  onClose,
  currentText,
  platform,
  onApply,
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
      }, 300),
    []
  );

  // Fetch templates
  const { data: templatesData, isLoading } = useQuery({
    queryKey: ['templates-apply', search, selectedCategory],
    queryFn: async () => {
      const response = await templatesAPI.list({
        limit: 50,
        search: search || undefined,
        category: selectedCategory || undefined,
        sort_by: 'usage_count',
      });
      // Backend returns an envelope: { templates: [...], total, page, limit }
      // Older code paths may return a bare array — handle both.
      const payload = response?.data;
      if (Array.isArray(payload)) {
        return payload as Template[];
      }
      if (payload && Array.isArray((payload as { templates?: Template[] }).templates)) {
        return (payload as { templates: Template[] }).templates;
      }
      return [] as Template[];
    },
    enabled: isOpen,
  });

  // Apply template mutation
  const applyMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const response = await templatesAPI.apply(templateId, {
        existing_text: currentText,
        platform,
      });
      return response.data as ApplyTemplateResult;
    },
    onSuccess: (data) => {
      onApply(data);
      toast.success(t('template_applied', 'Template applied'));
      onClose();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('apply_error', 'Failed to apply template')));
    },
  });

  const rawTemplates = templatesData as unknown;
  const templates: Template[] = Array.isArray(rawTemplates)
    ? (rawTemplates as Template[])
    : rawTemplates &&
        typeof rawTemplates === 'object' &&
        Array.isArray((rawTemplates as { templates?: unknown }).templates)
      ? ((rawTemplates as { templates: Template[] }).templates)
      : [];

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay — dismisses the panel on click or Enter/Escape */}
      <div
        role="button"
        tabIndex={0}
        aria-label={t('close', 'Close')}
        className="fixed inset-0 z-40 bg-black/30 transition-opacity duration-200"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 bottom-0 z-50 w-full overflow-hidden bg-white shadow-2xl transition-transform duration-300 ease-out sm:w-96"
        style={{
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#e0ddd8] bg-white px-6 py-4">
          <h2 className="text-lg font-semibold text-[#37352f]">
            {t('templates_title', 'Templates')}
          </h2>
          <button
            onClick={onClose}
            className="n-btn-icon rounded-lg p-2 hover:bg-[#f5f3f0] transition-colors"
            aria-label={t('close_panel', 'Close panel')}
          >
            <X size={20} className="text-[#787774]" />
          </button>
        </div>

        {/* Search */}
        <div className="border-b border-[#e0ddd8] px-6 py-4">
          <input
            type="text"
            placeholder={t('search_templates', 'Search templates...')}
            onChange={(e) => debouncedSearch(e.target.value)}
            className="n-input w-full rounded-lg border border-[#e0ddd8] bg-white px-4 py-2 text-sm text-[#37352f] placeholder-[#787774] focus:border-[#2383e2] focus:outline-none focus:ring-1 focus:ring-[#2383e2]"
          />
        </div>

        {/* Category Filter Chips */}
        <div className="border-b border-[#e0ddd8] px-6 py-4">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === null
                  ? 'n-badge-blue bg-[#2383e2] text-white'
                  : 'bg-[#f5f3f0] text-[#37352f] hover:bg-[#e8e5df]'
              }`}
            >
              {t('all_templates', 'All')}
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  selectedCategory === cat
                    ? 'n-badge-blue bg-[#2383e2] text-white'
                    : 'bg-[#f5f3f0] text-[#37352f] hover:bg-[#e8e5df]'
                }`}
              >
                {t(`category_${cat.toLowerCase()}`, cat)}
              </button>
            ))}
          </div>
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#e0ddd8] border-t-[#2383e2]" />
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <p className="text-sm text-[#787774]">
                {t('no_templates', 'No templates found')}
              </p>
            </div>
          ) : (
            <div className="space-y-2 px-4 py-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="n-card group rounded-lg border border-[#e0ddd8] bg-white p-4 hover:border-[#d0ccc7] hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-[#37352f] truncate">
                        {template.name}
                      </h3>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="n-badge-blue inline-block rounded-full bg-[#e8f0ff] px-2 py-1 text-xs font-medium text-[#2383e2]">
                          {t(`category_${template.category.toLowerCase()}`, template.category)}
                        </span>
                        <span className="text-xs text-[#787774]">
                          {t('usage_count', '{{count}} uses', {
                            count: template.usage_count,
                          })}
                        </span>
                      </div>
                      <p className="mt-3 line-clamp-2 text-sm text-[#787774]">
                        {template.content}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          setPreviewId(previewId === template.id ? null : template.id)
                        }
                        className="n-btn-icon flex h-8 w-8 items-center justify-center rounded-lg bg-[#f5f3f0] text-[#787774] hover:bg-[#e8e5df] transition-colors"
                        aria-label={t('preview_template', 'Preview template')}
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => applyMutation.mutate(template.id)}
                        disabled={applyMutation.isPending}
                        className="n-btn n-btn-primary flex h-8 w-8 items-center justify-center rounded-lg bg-[#2383e2] text-white hover:bg-[#1a5fb8] transition-colors disabled:opacity-50"
                        aria-label={t('apply_template', 'Apply template')}
                      >
                        {applyMutation.isPending ? (
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        ) : (
                          '→'
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Preview */}
                  {previewId === template.id && (
                    <div className="mt-4 border-t border-[#e0ddd8] pt-4">
                      <p className="whitespace-pre-wrap text-sm text-[#37352f]">
                        {template.content}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-[#e0ddd8] bg-white px-6 py-4">
          <a
            href="/templates"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2383e2] hover:text-[#1a5fb8] transition-colors"
          >
            {t('manage_templates', 'Manage Templates')}
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </>
  );
};

export default TemplateApplyPanel;
